import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow } from '../../components/InvField';
import { getVendorReturn, confirmVendorReturn, cancelVendorReturn } from '../../api/vendorReturn';
import { getVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import usePoGrnLookup from '../../hooks/usePoGrnLookup';
import { hasDisplayValue } from '../../utils/poLifecycle';
import { formatDate, formatQty, formatCurrency } from '../../utils';
import { formatVendorReturnError, findVendorReturnStockShortfall, validateVendorReturnCancel } from '../../utils/vendorReturnEntity';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

/** ConfirmDialog action keys on this screen -> the permission each one needs. */
const DIALOG_ACTION_PERMISSION = {
  confirm: INV_ACTIONS.VENDOR_RETURN_CONFIRM,
  cancel: INV_ACTIONS.VENDOR_RETURN_CANCEL,
};

export default function VendorReturnDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const base = url.replace(/\/vendor-return\/.*/, '');
  const { resolveItem, resolveItemId } = useItemNames();
  const { resolveGrnNumber } = usePoGrnLookup();

  const [ret, setRet] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await getVendorReturn(id);
      setRet(r);
      const vendorsData = await getVendors().catch(() => []);
      setVendors(normalizeList(vendorsData));
    } catch (e) {
      setError(formatVendorReturnError(e, { resolveItemId, action: 'load' }));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [id]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  async function executeAction() {
    const required = DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    setActioning(true);
    try {
      if (dialog.action === 'cancel') {
        const cancelErr = validateVendorReturnCancel(reason);
        if (cancelErr) {
          toast.error(cancelErr.message);
          return;
        }
      }

      if (dialog.action === 'confirm') {
        const storeId = ret?.from_store_id || ret?.receiving_store_id;
        const actionLines = normalizeList(ret?.lines || ret?.items);
        const shortfall = await findVendorReturnStockShortfall({
          storeId,
          lines: actionLines,
          resolveItemId,
          actionVerb: 'confirm',
        });
        if (shortfall) {
          toast.error(shortfall.message);
          return;
        }
      }

      const scope = { tenant_id: ret?.tenant_id, hospital_id: ret?.hospital_id };
      if (dialog.action === 'confirm') await confirmVendorReturn(id, {}, scope);
      else if (dialog.action === 'cancel') await cancelVendorReturn(id, { reason }, scope);
      // API's success `message` isn't available here — unwrapInventoryPayload() (shared by
      // every inventory call) intentionally discards the envelope's `message` field.
      toast.success(dialog.action === 'confirm'
        ? 'Vendor return confirmed and stock deducted'
        : 'Vendor return cancelled');
      setDialog(null); setReason(''); load();
    } catch (e) {
      // Race condition: another user already confirmed/cancelled this return between
      // our load() and this action — refetch so the buttons re-gate off the real status.
      toast.error(formatVendorReturnError(e, {
        resolveItemId,
        action: dialog?.action || 'save',
      }));
      load();
    } finally { setActioning(false); }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!ret) return null;

  const status = ret.status;
  const lines = normalizeList(ret.lines || ret.items);
  const actionButtons = [];
  // DRAFT -> CONFIRMED or CANCELLED are both terminal transitions; CONFIRMED can still be
  // cancelled (does not reverse the stock deduction), CANCELLED cannot be acted on at all.
  if ((status === 'PENDING' || status === 'DRAFT') && can(INV_ACTIONS.VENDOR_RETURN_CONFIRM)) {
    actionButtons.push(
      <InvButton key="confirm" size="small" tone="mint"
        onClick={() => setDialog({
          action: 'confirm',
          title: 'Confirm Vendor Return',
          desc: 'This will deduct stock for every line on this return. This action cannot be undone.',
        })}>
        Confirm
      </InvButton>,
    );
  }
  if (['PENDING', 'DRAFT', 'CONFIRMED'].includes(status) && can(INV_ACTIONS.VENDOR_RETURN_CANCEL)) {
    actionButtons.push(
      <InvButton key="cancel" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({
          action: 'cancel',
          title: 'Cancel Vendor Return',
          desc: status === 'CONFIRMED'
            ? 'This return was already confirmed and stock has already been deducted. Cancelling will NOT restore stock automatically.'
            : 'Cancel this vendor return? This cannot be undone.',
          destructive: true,
          requireReason: true,
        })}>
        Cancel
      </InvButton>,
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Vendor Returns"
        title={ret.return_number || 'Vendor Return'}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(`${base}/vendor-returns`)}>
              Back to List
            </InvButton>
            {actionButtons}
          </>
        }
      />

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <SectionCard title="Return Details" sx={{ height: '100%' }} actions={<StatusChip status={status} />}>
            {hasDisplayValue(ret.return_type) && (
              <DetailRow label="Return Type">{ret.return_type}</DetailRow>
            )}
            {(ret.source_grn_id || ret.grn_number) && (
              <DetailRow label="Source GRN">
                {resolveGrnNumber(ret.source_grn_id, ret.grn_number)}
              </DetailRow>
            )}
            {hasDisplayValue(ret.vendor_id) && (
              <DetailRow label="Vendor">{resolveVendorName(ret.vendor_id)}</DetailRow>
            )}
            {hasDisplayValue(ret.return_date) && (
              <DetailRow label="Return Date">{formatDate(ret.return_date)}</DetailRow>
            )}
            {ret.total_amount != null && ret.total_amount !== '' && (
              <DetailRow label="Total Amount">{formatCurrency(ret.total_amount)}</DetailRow>
            )}
            {hasDisplayValue(ret.reason) && (
              <DetailRow label="Reason">{ret.reason}</DetailRow>
            )}
            {hasDisplayValue(ret.created_at) && (
              <DetailRow label="Created">{formatDate(ret.created_at)}</DetailRow>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Return Lines" sx={{ height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 420,
                  '& td, & th': {
                    borderColor: IPD_COLORS.border,
                    color: IPD_COLORS.textPrimary,
                    py: 1.25,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 180 }}>Item</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Return Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Batch</TableCell>
                    {ret.return_type === 'REPLACEMENT' && (
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Replacement</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                        No lines
                      </TableCell>
                    </TableRow>
                  ) : lines.map((line, i) => (
                    <TableRow key={line.id || i}>
                      <TableCell sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                        {resolveItem(line)}
                      </TableCell>
                      <TableCell align="right">{formatQty(line.return_quantity)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {hasDisplayValue(line.batch_number) ? line.batch_number : '—'}
                      </TableCell>
                      {ret.return_type === 'REPLACEMENT' && (
                        <TableCell><StatusChip status={line.replacement_status || 'AWAITING'} /></TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {ret.return_type === 'REPLACEMENT' && status === 'CONFIRMED' && (
        <Alert severity="info" sx={{ mt: 2.5 }}
          action={
            <InvButton size="small" variant="outlined" tone="teal"
              onClick={() => history.push(`${base}/vendor-returns/awaiting-replacement`)}>
              Track Replacements
            </InvButton>
          }>
          Awaiting replacement from vendor. Receive the physical stock via Rejected Items →
          Create Replacement GRN — note the line status here stays AWAITING regardless, since
          there's no backend endpoint yet to mark a vendor-return line as received.
        </Alert>
      )}

      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
